import * as React from "react"
import { createPortal } from "react-dom"
import { Check, Copy, Play, X } from "lucide-react"

import { UserBlobatar } from "@/components/chat/blobatar"
import {
  MessageMenu,
  MessageToolbar,
  type MessageActionHandlers,
} from "@/components/chat/message-actions"
import { MessageReactions } from "@/components/chat/reactions"
import { ReplyQuote } from "@/components/chat/reply-quote"
import { useMessagePress } from "@/components/chat/use-message-press"
import { Bubble, BubbleContent } from "@/components/ui/bubble"
import { Button } from "@/components/ui/button"
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageGroup,
  MessageHeader,
} from "@/components/ui/message"
import { attachmentSrc } from "@/chat/auth"
import type { ThreadRow } from "@/chat/format"
import { formatTime, isEmojiOnly, messageAnchorId } from "@/chat/format"
import {
  isVideo,
  type Attachment,
  type ReplySnapshot,
  type User,
} from "@/chat/protocol"
import { DOUBLE_TAP_REACTION } from "@/lib/emoji"
import { cn } from "@/lib/utils"

/**
 * A run of consecutive messages from one person. Your own sit right on the
 * primary fill; everyone else sits left on a muted surface with their avatar
 * closing the run, the way a group chat reads. The byline goes once, above the
 * first message, and a pending send is dimmed until the server echoes it.
 * Reactions sit under each message as chips; picking one, and replying,
 * happen over the message (`message-actions.tsx`). A reply carries the
 * message it answers as a quote above its own body, and that quote jumps to
 * the original.
 */
export function MessageRun({
  rows,
  selfId,
  participants,
  onReact,
  onReply,
  onJump,
}: {
  rows: ThreadRow[]
  selfId: string | null
  participants: User[]
  onReact: (messageId: string, emoji: string) => void
  /** Starts a reply to this row; the snapshot is what the composer quotes. */
  onReply: (snapshot: ReplySnapshot) => void
  /** Scrolls the thread to a quoted message. */
  onJump: (messageId: string) => void
}) {
  const mine = rows[0]?.isMine ?? false
  const last = rows.length - 1

  return (
    <MessageGroup>
      {rows.map((row, index) => (
        <MessageItem
          key={row.key}
          row={row}
          mine={mine}
          withAvatar={index === last}
          withHeader={index === 0}
          selfId={selfId}
          participants={participants}
          onReact={onReact}
          onReply={onReply}
          onJump={onJump}
        />
      ))}
    </MessageGroup>
  )
}

/**
 * One message. Its actions open from the message itself: a hover bar on a
 * mouse, a long press or right click for the full menu, and a double tap
 * that puts a heart on it. Under it only the reaction chips remain.
 */
function MessageItem({
  row,
  mine,
  withAvatar,
  withHeader,
  selfId,
  participants,
  onReact,
  onReply,
  onJump,
}: {
  row: ThreadRow
  mine: boolean
  withAvatar: boolean
  withHeader: boolean
  selfId: string | null
  participants: User[]
  onReact: (messageId: string, emoji: string) => void
  onReply: (snapshot: ReplySnapshot) => void
  onJump: (messageId: string) => void
}) {
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)

  const id = row.id
  const mineReactions = React.useMemo(
    () =>
      new Set(
        row.reactions
          .filter((r) => selfId !== null && r.userIds.includes(selfId))
          .map((r) => r.emoji)
      ),
    [row.reactions, selfId]
  )

  // A pending send has no id yet: nothing to react or reply to, only text
  // to copy.
  const actions: MessageActionHandlers = {
    mine: mineReactions,
    copyText: row.text || undefined,
    onReact: id === null ? undefined : (emoji) => onReact(id, emoji),
    onReply:
      id === null
        ? undefined
        : () =>
            onReply({
              id,
              userId: row.userId,
              name: row.name,
              text: row.text,
              hasAttachments: row.attachments.length > 0,
              hasVideo: row.attachments.some(isVideo),
            }),
  }
  const hasActions = !!(actions.onReact || actions.onReply || actions.copyText)

  const press = useMessagePress({
    onOpen: () => {
      if (hasActions) setMenuAnchor(bodyRef.current)
    },
    onDoubleTap: actions.onReact
      ? () => actions.onReact!(DOUBLE_TAP_REACTION)
      : undefined,
  })

  return (
    <Message
      // The anchor a quote jumps to. `scroll-mt` keeps the message clear of
      // the header it would otherwise land under.
      id={id ? messageAnchorId(id) : undefined}
      align={mine ? "end" : "start"}
      className="scroll-mt-4"
    >
      {!mine && (
        <MessageAvatar>
          {withAvatar && <UserBlobatar name={row.name} className="size-8" />}
        </MessageAvatar>
      )}

      <MessageContent>
        {withHeader && (
          <MessageHeader>
            {mine ? "Вы" : row.name} · {formatTime(row.at)}
          </MessageHeader>
        )}

        {/* The press target. Full width, so the hover bar lines up with the
            message's own edge and a long press beside a short bubble still
            counts. On a touch screen a long press is ours, not the
            browser's text selection: "Копировать текст" replaces it. */}
        <div
          ref={bodyRef}
          data-slot="message-body"
          {...press}
          className={cn(
            "relative flex w-full min-w-0 touch-manipulation flex-col items-start gap-2 [-webkit-touch-callout:none] group-data-[align=end]/message:items-end pointer-coarse:select-none",
            "transition-transform duration-150",
            menuAnchor && "scale-[1.02]"
          )}
        >
          <MessageToolbar {...actions} />

          {row.replyTo && (
            <ReplyQuote
              snapshot={row.replyTo}
              onJump={() => onJump(row.replyTo!.id)}
              className={cn(
                "mb-1 max-w-[85%] rounded-sm",
                mine ? "text-primary" : "text-muted-foreground"
              )}
            />
          )}

          {row.attachments.length > 0 && (
            <MediaGrid attachments={row.attachments} dimmed={!row.delivered} />
          )}

          {row.body === null ? null : row.body.kind === "text" ? (
            <Bubble
              variant={
                isEmojiOnly(row.body.text)
                  ? "ghost"
                  : mine
                    ? "default"
                    : "muted"
              }
              className={cn(!row.delivered && "opacity-60")}
            >
              <BubbleContent
                className={cn(
                  "whitespace-pre-wrap",
                  isEmojiOnly(row.body.text) && "text-4xl leading-tight"
                )}
              >
                {row.body.text}
              </BubbleContent>
            </Bubble>
          ) : (
            <CodeCard
              lang={row.body.lang}
              text={row.body.text}
              dimmed={!row.delivered}
            />
          )}
        </div>

        {id !== null && (
          <MessageReactions
            reactions={row.reactions}
            selfId={selfId}
            participants={participants}
            onToggle={(emoji) => onReact(id, emoji)}
          />
        )}

        {!row.delivered && <MessageFooter>Отправка…</MessageFooter>}
      </MessageContent>

      {menuAnchor && (
        <MessageMenu
          anchor={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          {...actions}
        />
      )}
    </Message>
  )
}

/**
 * A pending row previews the local file (a `blob:` URL) until the server's
 * copy replaces it; everything else is fetched from the server.
 */
function mediaSrc(attachment: Attachment): string {
  return attachment.url.startsWith("blob:")
    ? attachment.url
    : attachmentSrc(attachment)
}

/**
 * Pictures and videos in a message. One gets the width it needs; several
 * share a two-column grid. The intrinsic size is set from the metadata so the
 * thread does not jump as they arrive. A picture opens in the lightbox; a
 * video plays in place with the browser's own controls, fullscreen included.
 */
function MediaGrid({
  attachments,
  dimmed,
}: {
  attachments: Attachment[]
  dimmed: boolean
}) {
  const [open, setOpen] = React.useState<Attachment | null>(null)
  const single = attachments.length === 1

  return (
    <>
      <div
        className={cn(
          "max-w-[85%] gap-1 group-data-[align=end]/message:self-end",
          single ? "flex" : "grid grid-cols-2",
          dimmed && "opacity-60"
        )}
      >
        {attachments.map((attachment) =>
          isVideo(attachment) ? (
            <VideoTile
              key={attachment.id}
              attachment={attachment}
              single={single}
            />
          ) : (
            <button
              key={attachment.id}
              type="button"
              onClick={() => setOpen(attachment)}
              aria-label="Открыть фото"
              className={cn(
                "overflow-hidden rounded-lg bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring",
                single ? "max-h-80 max-w-72" : "aspect-square w-36"
              )}
            >
              <img
                src={mediaSrc(attachment)}
                width={attachment.width}
                height={attachment.height}
                alt=""
                loading="lazy"
                decoding="async"
                className={cn(
                  "block h-full w-full",
                  single ? "object-contain" : "object-cover"
                )}
                style={
                  single
                    ? {
                        aspectRatio: `${attachment.width} / ${attachment.height}`,
                      }
                    : undefined
                }
              />
            </button>
          )
        )}
      </div>

      {open && <Lightbox attachment={open} onClose={() => setOpen(null)} />}
    </>
  )
}

/**
 * A video in the thread. Only the metadata is fetched until it is played —
 * the `#t` fragment makes Safari, which otherwise shows nothing, paint the
 * first frame as the poster. The server serves byte ranges, so seeking and
 * starting playback do not wait for the whole file.
 */
function VideoTile({
  attachment,
  single,
}: {
  attachment: Attachment
  single: boolean
}) {
  // Chrome hides the controls of a paused video until it is hovered, which
  // leaves it looking like a picture; the badge says it plays.
  const [playing, setPlaying] = React.useState(false)

  return (
    <div className="relative">
      <video
        src={`${mediaSrc(attachment)}#t=0.001`}
        width={attachment.width}
        height={attachment.height}
        controls
        playsInline
        preload="metadata"
        aria-label="Видео"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className={cn(
          "block rounded-lg bg-black",
          single
            ? "h-auto max-h-80 w-auto max-w-72"
            : "aspect-square w-36 object-cover"
        )}
        style={
          single
            ? { aspectRatio: `${attachment.width} / ${attachment.height}` }
            : undefined
        }
      />

      {!playing && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex size-10 items-center justify-center rounded-full bg-black/50">
            <Play className="size-5 fill-white text-white" />
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Full-size view, rendered into `document.body`.
 *
 * The portal is load-bearing, not tidiness: the transcript's scroller sets
 * `contain: content`, which makes it a containing block for `fixed`
 * descendants. An overlay rendered in place therefore sized itself to the
 * message list and was clipped by it — the picture came out smaller than the
 * window it was supposed to fill.
 *
 * The picture starts fitted to the window and zooms to its own pixels on a
 * click, which is the only way to read a screenshot on a phone; while zoomed
 * the overlay scrolls in both directions. Closes on Escape, on the backdrop,
 * or the corner button.
 */
function Lightbox({
  attachment,
  onClose,
}: {
  attachment: Attachment
  onClose: () => void
}) {
  const [zoomed, setZoomed] = React.useState(false)

  React.useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Фото"
      onClick={onClose}
      className="fixed inset-x-0 top-0 z-50 h-(--app-height,100dvh) overflow-auto overscroll-contain bg-black/95"
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        aria-label="Закрыть"
        className="fixed top-3 right-3 z-10 text-white hover:bg-white/10 hover:text-white"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
          right: "calc(env(safe-area-inset-right, 0px) + 0.75rem)",
        }}
      >
        <X className="size-5" />
      </Button>

      {/* A wrapper rather than centring the image directly: once zoomed the
          image is wider than the overlay, and a flex item that overflows its
          scroll container cannot be scrolled back to on the left. */}
      <div className="flex min-h-full w-fit min-w-full items-center justify-center p-4">
        <img
          src={mediaSrc(attachment)}
          width={attachment.width}
          height={attachment.height}
          alt=""
          onClick={(event) => {
            event.stopPropagation()
            setZoomed((current) => !current)
          }}
          className={cn(
            "block h-auto",
            zoomed
              ? "max-w-none cursor-zoom-out"
              : "max-h-[calc(var(--app-height,100dvh)-2rem)] w-auto max-w-full cursor-zoom-in object-contain"
          )}
        />
      </div>
    </div>,
    document.body
  )
}

function CodeCard({
  lang,
  text,
  dimmed,
}: {
  lang: string
  text: string
  dimmed: boolean
}) {
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    if (!copied) return

    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      // Clipboard access is denied outside a secure context. The code is still
      // selectable by hand, so there is nothing useful to report here.
    }
  }

  return (
    <Bubble
      variant="outline"
      className={cn("w-full max-w-[90%]", dimmed && "opacity-60")}
    >
      <BubbleContent className="w-full p-0">
        <div className="flex items-center justify-between border-b bg-muted py-1 pr-1 pl-3">
          <span className="font-mono text-[11px] text-muted-foreground">
            {lang}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={copied ? "Скопировано" : "Скопировать код"}
            onClick={() => void copy()}
          >
            {copied ? <Check /> : <Copy />}
          </Button>
        </div>
        <pre className="m-0 overflow-x-auto px-3 py-2 font-mono text-xs leading-relaxed">
          {text}
        </pre>
      </BubbleContent>
    </Bubble>
  )
}
